import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import './WorkspaceModal.css';

interface WorkspaceModalProps {
  open: boolean;
  title: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  onClose: () => void;
}

const WorkspaceModal: React.FC<WorkspaceModalProps> = ({
  open,
  title,
  children,
  footer,
  className,
  onClose,
}) => {
  useEffect(() => {
    if (!open) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  return createPortal(
    <>
      <div className="workspace-modal-backdrop" />
      <section
        aria-modal="true"
        className={['workspace-modal-panel', className].filter(Boolean).join(' ')}
        role="dialog"
      >
        <header className="workspace-modal-header">
          <div className="workspace-modal-title">{title}</div>
          <Button
            aria-label="Close"
            className="workspace-modal-close"
            icon={<CloseOutlined />}
            type="text"
            onClick={onClose}
          />
        </header>
        <div className="workspace-modal-body">{children}</div>
        {footer && <footer className="workspace-modal-footer">{footer}</footer>}
      </section>
    </>,
    document.body,
  );
};

export default WorkspaceModal;
